import React from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { getDateFormat } from "@utils/datePickerUtils";
import { Image, Anchor } from "hesus-ui-kit";

import {
  GridRow,
  GridCell,
  GridCellTitle,
  GridCellValue,
  GridCellPara,
  FlexSection,
  RecipientCard,
  RecipientInfo,
  RecipientName,
  RecipientEmail,
  RecipientNoEmail,
} from "../../InvoiceView.styled";
import { addressSingleLine } from "@utils/utils";
import { ColumnButton } from "@utils/extra-style";
import { avatarIcons, avatarIconsMr, avatarIconsMs } from "@utils/avatarInfo";

const DocumentSection = ({ t, invoiceData, countryList }) => {
  const getAvatarImage = (contact) => {
    const avatarNumber = (contact.contactId ?? 0) % 10;
    if (contact?.contactInfo?.courtesy === "MISTER") {
      return avatarIconsMr[avatarNumber] ?? avatarIcons[1];
    }
    if (contact?.contactInfo?.courtesy === "MISS") {
      return avatarIconsMs[avatarNumber] ?? avatarIcons[4];
    }
    return avatarIcons[avatarNumber] ?? avatarIcons[1];
  };

  const invoiceTypes = [
    invoiceData.isInterco && t("CLIENT_INVOICING_INVOICE_TYPE_INTERCO"),
    invoiceData.isRegularisation &&
      t("CLIENT_INVOICING_INVOICE_TYPE_ADJUSTMENT"),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <GridRow
        background="#008395"
        borderRadius="5px 5px 0 0px"
        column="1fr auto"
      >
        <GridCell fontWeight="700" fontSize="16px">
          {t("CLIENT_INVOICING_SECTION_DOC_INFO")}
        </GridCell>
        {invoiceData.sentAt && (
          <GridCell fontStyle="italic" fontWeight="700">
            {t("CLIENT_INVOICING_SENT_ON") +
              "  " +
              getDateFormat(invoiceData.sentAt, "YYYY-MM-DD")}
          </GridCell>
        )}
      </GridRow>
      <GridRow
        column="1fr"
        boxShadow="0px 1px 1px 1px #e5e5e5"
        margin="0 0 12px 0"
        padding="0px"
        gap="0px"
      >
        <GridRow column="75% 23%">
          <GridRow column="1fr" padding="0px" gap="1rem">
            <GridRow column="1fr 1fr" padding="0px">
              <GridRow column="auto 1fr" padding="0px" gap="10px">
                <GridCellTitle fontSize="15px">
                  {t("CLIENT_INVOICE_REFERENCE")} :
                </GridCellTitle>
                <GridCellValue fontSize="15px">
                  {invoiceData.documentNumber
                    ? invoiceData.documentNumber
                    : "-"}
                </GridCellValue>
              </GridRow>
              <GridRow column="auto 1fr" padding="0px" gap="10px">
                <GridCellTitle fontSize="15px">
                  {t("CLIENT_INVOICING_ATTACHED_INVOICE")} :
                </GridCellTitle>
                <GridCellValue fontSize="15px">
                  {invoiceData?.attachedInvoice?.id ? (
                    <Anchor
                      url={`/invoice/view/${invoiceData?.attachedInvoice?.id}`}
                      target="_blank"
                      rel="noreferrer"
                      styles={{
                        color: "#1bace4",
                        textDecoration: "underline",
                      }}
                    >
                      {`#${invoiceData.attachedInvoice.documentNumber}`}
                    </Anchor>
                  ) : (
                    "-"
                  )}
                </GridCellValue>
              </GridRow>
            </GridRow>
            <GridRow column="1fr 1fr" padding="0px">
              <GridRow column="auto 1fr" padding="0px" gap="10px">
                <GridCellTitle fontSize="15px">
                  {t("CLIENT_INVOICING_PERIOD")} :
                </GridCellTitle>
                <GridCellValue fontSize="15px">
                  {getDateFormat(invoiceData.periodStart, "YYYY-MM-DD") +
                    " - " +
                    getDateFormat(invoiceData.periodEnd, "YYYY-MM-DD")}
                </GridCellValue>
              </GridRow>
              <GridRow column="auto 1fr" padding="0px" gap="10px">
                <GridCellTitle fontSize="15px">
                  {t("CLIENT_INVOICING_ORDER_NUMBER")} :
                </GridCellTitle>
                <GridCellValue fontSize="15px">
                  {invoiceData.clientOrderNumber
                    ? invoiceData.clientOrderNumber
                    : "-"}
                </GridCellValue>
              </GridRow>
            </GridRow>
            <GridRow column="1fr 1fr" padding="0px">
              <GridRow column="auto 1fr" padding="0px" gap="10px">
                <GridCellTitle fontSize="15px">
                  {t("CLIENT_INVOICING_INVOICE_TYPE")} :
                </GridCellTitle>
                <GridCellValue fontSize="15px">{invoiceTypes}</GridCellValue>
              </GridRow>
              <GridRow column="auto 1fr" padding="0px" gap="10px">
                <GridCellTitle fontSize="15px">
                  {t("CLIENT_INVOICING_IS_SUBCONTRACTING")} :
                </GridCellTitle>
                <GridCellValue fontSize="15px">
                  {invoiceData.isSubcontracting
                    ? t("GENERAL_YES")
                    : t("GENERAL_NO")}
                </GridCellValue>
              </GridRow>
            </GridRow>
          </GridRow>
          <GridRow column="1fr" padding="0px">
            <ColumnButton
              border="#EBF7ED"
              background="#EBF7ED"
              marginRight="20px"
              float="right"
              height="50px"
              padding="5px 20px 5px 20px"
            >
              <GridCellTitle fontSize="15px">
                {t("CLIENT_INVOICING_DUE_DATE")} :{" "}
                {invoiceData.dueDate
                  ? getDateFormat(invoiceData.dueDate, "YYYY-MM-DD")
                  : ""}
              </GridCellTitle>
              <GridCellPara>{invoiceData?.paymentTerm?.label}</GridCellPara>
            </ColumnButton>
          </GridRow>
        </GridRow>

        <GridRow column="1fr 1fr" alignItems="stretch">
          <FlexSection>
            <GridRow
              background="#0A4986"
              borderRadius="5px 5px 0 0px"
              column="1fr"
              padding="8px 10px"
            >
              <GridCell fontWeight="700" fontSize="15px">
                {t("CLIENT_INVOICING_SELECTED_COMPANY")}
              </GridCell>
            </GridRow>
            <GridRow
              column="1fr"
              boxShadow="0px 1px 1px 1px #e5e5e5"
              margin="0 0 12px 0"
              padding="0px"
            >
              <GridRow column="1fr" gap="1rem">
                <GridRow column="auto 1fr" gap="10px" padding="0px">
                  <GridCellTitle>{t("CLIENT_INVOICE_COMPANY")} :</GridCellTitle>
                  <GridCellValue>
                    {invoiceData?.company?.id ? (
                      <Anchor
                        url={`/company/view/${invoiceData?.company?.id}/general-info`}
                        target="_blank"
                        rel="noreferrer"
                        styles={{
                          color: "#1bace4",
                          textDecoration: "underline",
                        }}
                      >
                        {`#${invoiceData.company.id} - ${invoiceData.company.name}`}
                      </Anchor>
                    ) : (
                      "-"
                    )}
                  </GridCellValue>
                </GridRow>
                <GridRow column="auto 1fr" gap="10px" padding="0px">
                  <GridCellTitle>
                    {t("CLIENT_INVOICE_IDENTICATION_NUMBER")} :
                  </GridCellTitle>
                  <GridCellValue>
                    {invoiceData?.company?.id
                      ? invoiceData?.company?.identificationNumber
                      : "-"}
                  </GridCellValue>
                </GridRow>
                <GridRow column="auto 1fr" gap="10px" padding="0px">
                  <GridCellTitle>
                    {t("CLIENT_INVOICE_COMPANY_ADDRESS")} :
                  </GridCellTitle>
                  <GridCellValue>
                    {invoiceData?.company?.id ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: addressSingleLine(
                            invoiceData?.country?.id,
                            invoiceData?.company?.billingAddress,
                            countryList
                          ),
                        }}
                      />
                    ) : (
                      "-"
                    )}
                  </GridCellValue>
                </GridRow>
              </GridRow>
            </GridRow>
          </FlexSection>
          <FlexSection>
            <GridRow
              background="#0A4986"
              borderRadius="5px 5px 0 0px"
              column="1fr"
              padding="8px 10px"
            >
              <GridCell fontWeight="700" fontSize="15px">
                {t("CLIENT_INVOICING_SELECTED_SITE")}
              </GridCell>
            </GridRow>
            <GridRow
              column="1fr"
              boxShadow="0px 1px 1px 1px #e5e5e5"
              margin="0 0 12px 0"
              padding="0px"
            >
              <GridRow column="1fr" gap="1rem">
                <GridRow column="auto 1fr" gap="10px" padding="0px">
                  <GridCellTitle>{t("CLIENT_INVOICE_SITE")} : </GridCellTitle>
                  <GridCellValue>
                    {invoiceData?.site?.id ? (
                      <Anchor
                        url={`/site/view/${invoiceData?.site?.id}/general-info`}
                        target="_blank"
                        rel="noreferrer"
                        styles={{
                          color: "#1bace4",
                          textDecoration: "underline",
                        }}
                      >
                        {`#${invoiceData?.site?.id} - ${invoiceData?.site?.name}`}
                      </Anchor>
                    ) : (
                      t("CLIENT_INVOICING_NO_SITE")
                    )}
                  </GridCellValue>
                </GridRow>
                <GridRow column="auto 1fr" gap="10px" padding="0px">
                  <GridCellTitle>
                    {t("CLIENT_INVOICE_SITE_ADDRESS")} :
                  </GridCellTitle>
                  <GridCellValue>
                    {invoiceData?.site?.id ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: addressSingleLine(
                            invoiceData?.site?.address?.country,
                            invoiceData?.site?.address,
                            countryList
                          ),
                        }}
                      />
                    ) : (
                      "-"
                    )}
                  </GridCellValue>
                </GridRow>
                <GridRow></GridRow>
              </GridRow>
            </GridRow>
          </FlexSection>
        </GridRow>
        {!!invoiceData.isSentByEmail && invoiceData?.recipients?.length > 0 && (
          <>
            <GridRow padding="0px 16px">
              <GridCellTitle fontSize="15px">
                {t("CLIENT_INVOICING_SELECTED_RECIPIENTS")} :{" "}
              </GridCellTitle>
            </GridRow>

            <GridRow column="repeat(3, 1fr)" padding="5px 12px 16px">
              {invoiceData?.recipients?.map((contact) => {
                return (
                  <RecipientCard key={contact.id}>
                    <Image
                      src={getAvatarImage(contact)}
                      alt="Avatar"
                      styles={{ height: "40px", width: "40px" }}
                    />
                    <RecipientInfo>
                      <RecipientName>
                        {`${contact.contactInfo.firstName || ""} ${
                          contact.contactInfo.lastName || ""
                        }`.trim()}
                      </RecipientName>
                      {contact.contactInfo.email ? (
                        <RecipientEmail>
                          {contact.contactInfo.email}
                        </RecipientEmail>
                      ) : (
                        <RecipientNoEmail>
                          {t("CLIENT_INVOICING_CONTACT_NO_EMAIL")}
                        </RecipientNoEmail>
                      )}
                    </RecipientInfo>
                  </RecipientCard>
                );
              })}
            </GridRow>
          </>
        )}
        <GridRow column="auto 1fr" gap="10px">
          <GridCellTitle fontSize="15px">
            {t("CLIENT_INVOICING_BANK_ACCOUNT")} :{" "}
          </GridCellTitle>
          <GridCellValue fontSize="15px">
            {invoiceData?.bankAccount?.id
              ? `${invoiceData.bankAccount.label} (${invoiceData.bankAccount.bankName} - ${invoiceData.bankAccount.iban} / ${invoiceData.bankAccount.bic})`
              : "-"}
          </GridCellValue>
        </GridRow>
      </GridRow>
    </>
  );
};

DocumentSection.propTypes = {
  t: PropTypes.func.isRequired,
};

DocumentSection.defaultProps = {};

export default withTranslation()(DocumentSection);
